import { useToggle } from 'ahooks'
import { Button, Col, Form, FormInstance, Modal, Row, notification } from 'antd'
import { useAtom } from 'jotai'
import { isNil } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { DepartmentTreeTransfer, isIdPureNumber } from '@/components/department-tree-transfer.component'
import { onDepartmentUserCheck } from '@/components/department-tree-with-users.component'
import { useOnSetSpecialProjectCache } from '@/components/special-project-local-storage-modal'
import { SpecialProjectFormLocalStorageKey } from '@/schemas'

import { departmentsTreeAtom } from '../../department'
import { DepartmentUser } from '../../project/project-create/project-form.component'
import { userInfoAtom, usersAtom } from '../../user'
import { Production, Stage } from '../production.schema'
import { ProductionBackgroundForm } from './production-background-form.component'
import { ProductionBaseInfoForm } from './production-baseInfo-form.component'
import { ProductionResultTimeForm } from './production-result-time-form.component'
import { ProductionTargetForm } from './production-target-form.component'
import { ProductionFromValues, getProductionFormValues, onProductionFormFinish } from './util'

const validateMessages = {
  required: '${label}是必选填项!',
}

export enum ProductionFormPartType {
  BASIC = 'BASIC',
  BACKGROUND = 'BACKGROUND',
  TARGET = 'TARGET',
  RESULT_TIME = 'RESULT_TIME',
}

export const ProductionForm: React.FC<{
  form: FormInstance<any>
  production?: Production
  formPartType?: ProductionFormPartType
  onSubmit: (data: ProductionFromValues) => Promise<Production>
  loading?: boolean
}> = ({ production, form, onSubmit, loading, formPartType }) => {
  const { t } = useTranslation()

  const [departments] = useAtom(departmentsTreeAtom)
  const [userInfo] = useAtom(userInfoAtom)

  const [isExpectDeliveryTimeInit, setIsExpectDeliveryTimeInit] = React.useState(!!production)
  const projectType = Form.useWatch('projectType', form)
  const [departmentUsers, setDepartmentUsers] = React.useState<DepartmentUser[]>([])

  const { onSetCache, setFormCache } = useOnSetSpecialProjectCache(SpecialProjectFormLocalStorageKey.PRODUCTION)
  const [modalVisible, { toggle: modalToggle }] = useToggle()

  const readPermissions = production?.readPermissions || []
  React.useEffect(() => {
    const defaultCheckedKeys =
      readPermissions.length > 0
        ? readPermissions
            .map((permission) => {
              // departmentId
              if (isIdPureNumber(permission)) {
                return Number(permission)
              }
              // userId
              const user = users.find((user) => user.email === permission)
              return user ? `${user.departmentIds[0]}-${user.email}` : null
            })
            .filter(Boolean)
            .map((id) => id!)
        : []
    onDepartmentUserCheck(defaultCheckedKeys, departments, users, setDepartmentUsers)
  }, [])

  const [user] = useAtom(userInfoAtom)

  React.useEffect(() => {
    if (!production) {
      form.setFieldsValue({
        email: user.email,
        stage: Stage.新产品,
      })
    } else {
      const data = getProductionFormValues(production)
      form.setFieldsValue(data)
    }
  }, [production])

  const [users] = useAtom(usersAtom)

  const onValuesChange = (data: any, v: any) => {
    // 创建时，缓存数据
    if (!production) {
      onSetCache(Object.assign({}, v))
    }
    const { expectDeliveryTime } = data

    if (expectDeliveryTime && !isExpectDeliveryTimeInit) {
      setIsExpectDeliveryTimeInit(true)
    }
  }

  return (
    <>
      {!production ? (
        <>
          <Row>
            <Col className='pt-8'>
              <h2>{t('production.createProduction')}</h2>
            </Col>
          </Row>
          <Row>
            <div className='mb-4'>
              <div>{t('production.form.description1')}</div>
              <div>{t('production.form.description2')}</div>
              <div>{t('production.form.description3')}</div>
            </div>
          </Row>
        </>
      ) : null}
      <Form
        preserve={false}
        onFinish={() => {
          onProductionFormFinish(form, t, onSubmit)().finally(() => {
            setFormCache(undefined)
          })
        }}
        onFinishFailed={(errorInfo) => {
          if (errorInfo && errorInfo.values) {
            errorInfo.errorFields.forEach(({ errors }) => {
              notification.error({
                style: { backgroundColor: '#FFEBE8' },
                message: <span>{errors.join('\n')}</span>,
              })
            })
          }
        }}
        form={form}
        layout='vertical'
        validateMessages={validateMessages}
        onValuesChange={onValuesChange}
      >
        {(isNil(formPartType) || formPartType === ProductionFormPartType.BASIC) && (
          <ProductionBaseInfoForm production={production} />
        )}
        {(isNil(formPartType) || formPartType === ProductionFormPartType.BACKGROUND) && (
          <ProductionBackgroundForm production={production} />
        )}
        {(isNil(formPartType) || formPartType === ProductionFormPartType.TARGET) && (
          <ProductionTargetForm production={production} userInfo={userInfo} projectType={projectType} />
        )}
        {(isNil(formPartType) || formPartType === ProductionFormPartType.RESULT_TIME) && <ProductionResultTimeForm />}

        <Form.Item>
          <Button hidden={!!production} loading={loading} type='primary' htmlType='submit'>
            {t('create')}
          </Button>
        </Form.Item>
      </Form>

      <Modal
        width={700}
        visible={modalVisible}
        closable={false}
        cancelButtonProps={{ style: { display: 'none' } }}
        onOk={() => modalToggle()}
        destroyOnClose
      >
        <DepartmentTreeTransfer
          users={users}
          departments={departments}
          userInfo={userInfo}
          defaultPermissions={readPermissions}
          departmentUsers={departmentUsers}
          setDepartmentUsers={setDepartmentUsers}
        />
      </Modal>
    </>
  )
}
